/**
 * Where a lesson's objects stand in a real room, and how a child moves them.
 *
 * In AR the room's origin is wherever the headset was when the session
 * started, so objects placed at the origin stand inside the student. A
 * skeleton that arrives in the middle of a child is the first thing they will
 * report, and it is not what the lesson is about.
 *
 * So an AR lesson arrives **in front of the viewer, facing them**, and is
 * then theirs to move: onto a desk, shrunk to fit it, or back on the floor at
 * life size. Two hands apart make it bigger, the way every other thing in a
 * headset works.
 *
 * Arithmetic only, no three.js, so the rules are tested rather than
 * discovered while wearing a headset.
 */

export type Point = readonly [number, number, number];

/**
 * How far in front a lesson arrives.
 *
 * Far enough to see a standing skeleton whole without stepping back, close
 * enough to reach out and take hold of a bone.
 */
export const ARRIVAL_METRES = 1.6;

/**
 * Small enough to sit on a desk, and no smaller: below this a skeleton is a
 * white smudge a child cannot pick bones off.
 */
export const MIN_SCALE = 0.25;

/** Big enough to walk into, and no bigger: past this it is through the wall. */
export const MAX_SCALE = 2.5;

/**
 * Where a lesson should stand, given where the viewer is and which way they
 * are looking.
 *
 * `yaw` follows WebXR: 0 is looking down -z. The returned `yaw` turns the
 * object's front (+z) towards the viewer, because a skeleton that greets its
 * first class with the back of its skull has failed at the only thing it had
 * to do.
 */
export function inFrontOf(
  viewer: { x: number; z: number; yaw: number },
  distance = ARRIVAL_METRES,
): { x: number; z: number; yaw: number } {
  return {
    x: viewer.x - Math.sin(viewer.yaw) * distance,
    z: viewer.z - Math.cos(viewer.yaw) * distance,
    // A model's front is its +z, and turned by the viewer's own yaw that +z
    // points straight back at them. Adding a half turn -- as this first did --
    // stood the skeleton with its back to the class.
    yaw: viewer.yaw,
  };
}

/** How far apart two hands are. */
export function spanBetween(a: Point, b: Point): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/**
 * The size something should be, part-way through a two-handed stretch.
 *
 * Relative to the span the hands started at and the size it already was, so a
 * child can resize in several goes without it jumping each time they take
 * hold again.
 *
 * Clamped at both ends because the failure is not cosmetic: an object that
 * shrinks to nothing or grows through the wall is one a ten-year-old cannot
 * get back, and there is no undo in a headset.
 */
export function pinchScale(input: { startSpan: number; span: number; startScale: number }): number {
  // Hands that started together carry no information about how much to grow
  // by. Guarded rather than divided, or the first millimetre of movement
  // sends the scale to infinity.
  const from = Math.max(input.startSpan, 0.02);
  const wanted = input.startScale * (input.span / from);

  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, wanted));
}

/**
 * The farthest a pointer may send something: past this it is outside any
 * classroom, through the wall.
 */
export const REACH_METRES = 8;

/** The nearest a pointer may pull something: within reach, not into you. */
export const NEAREST_METRES = 0.4;

/**
 * How hard tipping the pointer pulls. Tipping it down by this many radians
 * brings the object in to 1/e of its distance: about 16 degrees halves it,
 * which a wrist does without the arm moving.
 */
export const PULL_PER_RADIAN = 2.5;

const horizontalAngle = ([x, , z]: Point): number => Math.atan2(z, x);
const pitch = ([x, y, z]: Point): number => Math.atan2(y, Math.hypot(x, z));

/** An angle brought into (-pi, pi], so a turn goes the short way round. */
const shortest = (angle: number): number => {
  const turned = angle % (2 * Math.PI);
  if (turned > Math.PI) return turned - 2 * Math.PI;
  if (turned <= -Math.PI) return turned + 2 * Math.PI;
  return turned;
};

/**
 * Where a pointer drag puts something, from how far the pointer has turned
 * since the drag began (`from`) to now (`to`).
 *
 * Turning swings the object round the student; tipping down pulls it in,
 * tipping up sends it away, by a factor for every degree -- a skeleton at the
 * far wall comes in as quickly as one on the desk. Height is left alone.
 *
 * This replaced following the pointer at the handle's own level, which failed
 * for the handle that mattered: beside the skeleton's head, at eye height,
 * where the level runs almost along the ray and aiming down never meets it.
 */
export function aimMove(input: { eye: Point; object: Point; from: Point; to: Point }): Point {
  const { eye, object, from, to } = input;
  const dx = object[0] - eye[0];
  const dz = object[2] - eye[2];

  const distance = Math.min(
    REACH_METRES,
    Math.max(
      NEAREST_METRES,
      Math.hypot(dx, dz) * Math.exp(PULL_PER_RADIAN * (pitch(to) - pitch(from))),
    ),
  );
  const angle = Math.atan2(dz, dx) + shortest(horizontalAngle(to) - horizontalAngle(from));

  return [eye[0] + Math.cos(angle) * distance, object[1], eye[2] + Math.sin(angle) * distance];
}

/**
 * Where something goes when its grip has moved: by as much as the grip moved
 * across the floor, and not at all up or down. It slides; it does not climb.
 */
export function slide(object: Point, startGrip: Point, grip: Point): Point {
  return [object[0] + grip[0] - startGrip[0], object[1], object[2] + grip[2] - startGrip[2]];
}

/**
 * The size something should be when its resize handle has been lifted or
 * lowered: in proportion to the handle's height above the object's base. Lift
 * it to twice its height and the object doubles.
 *
 * Clamped for the same reason as `pinchScale`: something shrunk to nothing or
 * grown through the ceiling is something a child cannot get back.
 */
export function scaleFromLift(input: {
  startScale: number;
  baseY: number;
  startY: number;
  nowY: number;
}): number {
  // A handle grabbed at the base itself carries no information about how much
  // to grow by; guarded rather than divided.
  const from = Math.max(input.startY - input.baseY, 0.02);
  const wanted = input.startScale * ((input.nowY - input.baseY) / from);
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, wanted));
}

/**
 * The highest surface something may be stood on: a lab bench. Higher up, a hit
 * straight down is a shelf, a head or a hand reaching out.
 */
export const HIGHEST_SURFACE_METRES = 1.1;

/** How flat a surface must be to stand on: within 15 degrees of level. */
const LEVEL_ENOUGH = Math.cos((15 * Math.PI) / 180);

/**
 * Whether a surface found straight below something is one it can stand on:
 * the floor or a table top, level, not a wall or the side of a desk, and not
 * higher than a workbench. `up` is how much the surface faces upwards, 1 for
 * perfectly level.
 */
export function standsOn(surface: { y: number; up: number }): boolean {
  return surface.up >= LEVEL_ENOUGH && surface.y >= -0.2 && surface.y <= HIGHEST_SURFACE_METRES;
}

/** How close to the spot a line of sight must land to count as the answer. */
const ON_THE_SPOT = 0.05;

/**
 * What a hit along a line of sight says about the surface at `target`: the
 * height to stand on if it landed on the spot, the height to aim at next if it
 * met something short of it or past it, or nothing if it met something no one
 * could stand on.
 */
export function readSurface(
  target: { x: number; z: number },
  hit: { x: number; y: number; z: number; up: number },
): { standsAt: number } | { tryHeight: number } | undefined {
  if (!standsOn(hit)) return undefined;
  if (Math.hypot(hit.x - target.x, hit.z - target.z) <= ON_THE_SPOT) return { standsAt: hit.y };
  return { tryHeight: hit.y };
}

/**
 * How far to move something `by` metres to the right of the line from `eye`
 * to `at`, across the floor: an offset, not a position. Facing -z, the right
 * is +x.
 */
export function sideStep(eye: Point, at: Point, by: number): Point {
  const dx = at[0] - eye[0];
  const dz = at[2] - eye[2];
  const length = Math.hypot(dx, dz) || 1;
  return [(-dz / length) * by, 0, (dx / length) * by];
}
